import { defaultCityRadiusKm } from '@/constants'
import { useCampaignData, useCommonCampaignCardProps } from '@/hooks'
import { toLocalDateString, translatePaymentError } from '@/lib'
import {
	appendToastAtom,
	CampaignTargeting,
	ConfirmationDialog,
	ConfirmationDialogRef,
	NamedObject,
	useToastStoreContext,
} from '@/ui'
import {
	CityData,
	CountryData,
	CreateMetaAdStructureData,
	InterestData,
	useCampaignLaunchLaunch,
	useFeedsShow,
	useMetaAdStructureFacebookDetails,
	useMetaAdStructureGetMetaAdCampaignByCampaignId,
	useMetaInterestIndex,
	useMetaLocationSearchCities,
	useMetaLocationSearchCountries,
	useMetaReachEstimateEstimate,
} from '@jobilla/nexus-sdk'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useDebounce } from '@uidotdev/usehooks'
import { useSetAtom } from 'jotai'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute(
	'/_auth/company/$companyId/$campaignId/target',
)({
	component: RouteComponent,
})

type TargetingOptionsState = {
	targetingOptions: CreateMetaAdStructureData['targetingOptions']
}

type DateOverride = {
	start: Date
	end: Date
}

function RouteComponent() {
	const params = Route.useParams()
	const common = useCommonCampaignCardProps(params.campaignId, params.companyId)
	const navigate = useNavigate()
	const toastStore = useToastStoreContext()
	const appendToast = useSetAtom(appendToastAtom, { store: toastStore })
	const { t, i18n } = useTranslation()
	const currencyFormatter = useMemo(
		() => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }),
		[i18n.language],
	)

	const feedAd = useFeedsShow(params.campaignId)
	const { campaign, isLoading: isLoadingCampaign } = useCampaignData({ campaignId: params.campaignId })

	const getFormLink = () => {
		return `${import.meta.env.VITE_FORM_URL}${campaign.formId}`
	}

	const [adCampaign, setAdCampaign] = useState<TargetingOptionsState>({
		targetingOptions: {
			interests: [],
			countries: [],
			cities: [],
		},
	})

	// Prefills the city suggestions from the campaign's own location selections
	// exactly once, right after the campaign has actually loaded — never from
	// the useState initializer, which would otherwise run before the campaign
	// data has arrived and freeze on an empty list.
	const hasSeededCitiesRef = useRef(false)
	useEffect(() => {
		if (hasSeededCitiesRef.current || isLoadingCampaign) { return }
		hasSeededCitiesRef.current = true
		setAdCampaign((prev) => ({
			...prev,
			targetingOptions: {
				...prev.targetingOptions,
				cities: campaign.adCampaignLocations.map((city) => ({
					...city,
					radius: defaultCityRadiusKm,
				})),
			},
		}))
	}, [isLoadingCampaign, campaign.adCampaignLocations])

	const [overrideDatesEnabled, setOverrideDatesEnabled] = useState(false)
	const [dateOverride, setDateOverride] = useState<DateOverride | null>(null)

	const displayedStartDate = overrideDatesEnabled ? dateOverride?.start ?? campaign.startedAt : campaign.startedAt
	const displayedEndDate = overrideDatesEnabled ? dateOverride?.end ?? campaign.endsAt : campaign.endsAt

	const [citySearch, setCitySearch] = useState('')
	const [countrySearch, setCountrySearch] = useState('')
	const [interestSearch, setInterestSearch] = useState('')
	const debouncedInterestSearch = useDebounce(interestSearch, 300)
	const debouncedCitySearch = useDebounce(citySearch, 300)
	const debouncedCountrySearch = useDebounce(countrySearch, 300)

	const errorConfirmationRef = useRef<ConfirmationDialogRef>(null)
	const launchCampaign = useCampaignLaunchLaunch(
		{
			mutationKey: ['launch-campaign'],
			onSuccess: () => {
				void navigate({
					to: '/company/$companyId/$campaignId',
					params: {
						companyId: params.companyId,
						campaignId: params.campaignId,
					},
					replace: true,
				})
			},
			onError: (error) => {
				errorConfirmationRef.current?.confirm({
					title: 'Launch failed',
					message: translatePaymentError(error, t, currencyFormatter)
						|| 'Something went wrong while launching your campaign. Please review your details and try again.',
					confirmText: 'OK',
					cancelText: '',
				}).catch(() => {})
			},
		},
	)

	const interests = useMetaInterestIndex({
		search: debouncedInterestSearch,
	}, {
		enabled: debouncedInterestSearch.length > 0,
		retry: false,
	})

	const countries = useMetaLocationSearchCountries({
		query: debouncedCountrySearch,
	}, {
		enabled: debouncedCountrySearch.length > 0,
		retry: false,
	})

	const cities = useMetaLocationSearchCities(
		{
			query: debouncedCitySearch,
		},
		{
			enabled: debouncedCitySearch.length > 0,
			retry: false,
		},
	)

	// Boost city suggestions matching the countries of pre-filled campaign locations
	const preferredCountryCodes = useMemo(
		() => new Set(campaign.adCampaignLocations.map((c) => c.country.id.toUpperCase())),
		[campaign.adCampaignLocations],
	)

	const sortedCitySuggestions = useMemo(() => {
		const raw = cities.data ?? []
		if (preferredCountryCodes.size === 0) { return raw }
		return [...raw].sort((a, b) => {
			const aMatch = a.country?.id ? preferredCountryCodes.has(a.country.id.toUpperCase()) : false
			const bMatch = b.country?.id ? preferredCountryCodes.has(b.country.id.toUpperCase()) : false
			if (aMatch && !bMatch) { return -1 }
			if (!aMatch && bMatch) { return 1 }
			return 0
		})
	}, [cities.data, preferredCountryCodes])

	const reachEstimate = useMetaReachEstimateEstimate(
		{
			interests: adCampaign.targetingOptions.interests,
			countries: adCampaign.targetingOptions.countries,
			cities: adCampaign.targetingOptions.cities,
		},
		{
			enabled: (adCampaign.targetingOptions.interests?.length ?? 0) > 0
				|| (adCampaign.targetingOptions.countries?.length ?? 0) > 0
				|| (adCampaign.targetingOptions.cities?.length ?? 0) > 0,
			retry: false,
		},
	)

	const handleToggleOverrideDates = (enabled: boolean) => {
		setOverrideDatesEnabled(enabled)
		if (enabled && !dateOverride && campaign.startedAt && campaign.endsAt) {
			setDateOverride({ start: campaign.startedAt, end: campaign.endsAt })
		}
	}

	const handleStartDateChange = (date: Date) => {
		setDateOverride((prev) => ({ start: date, end: prev?.end ?? campaign.endsAt ?? date }))
	}

	const handleEndDateChange = (date: Date) => {
		setDateOverride((prev) => ({ start: prev?.start ?? campaign.startedAt ?? date, end: date }))
	}

	const handleInterestSearchChange = (value: string) => {
		setInterestSearch(value)
	}

	const handleCitySearchChange = (value: string) => {
		setCitySearch(value)
	}

	const handleCountrySearchChange = (value: string) => {
		setCountrySearch(value)
	}

	const handleInterestsChange = (interests: InterestData[]) => {
		const uniqueInterests = interests.filter((interest, index, array) =>
			array.findIndex(item => item.id === interest.id) === index
		)

		setAdCampaign((prev) => ({
			...prev,
			targetingOptions: {
				...prev.targetingOptions,
				interests: uniqueInterests,
			},
		}))
	}

	const handleCountriesChange = (countries: CountryData[]) => {
		const uniqueCountries = countries.filter((country, index, array) =>
			array.findIndex(item => item.id === country.id) === index
		)

		setAdCampaign((prev) => ({
			...prev,
			targetingOptions: {
				...prev.targetingOptions,
				countries: uniqueCountries,
			},
		}))
	}

	const handleCitiesChange = (cities: CityData[]) => {
		const citiesWithRadius = cities.map((city) => ({
			...city,
			radius: city.radius ?? defaultCityRadiusKm,
		}))

		const uniqueCities = citiesWithRadius.filter((city, index, array) =>
			array.findIndex(item => item.id === city.id) === index
		)

		setAdCampaign((prev) => ({
			...prev,
			targetingOptions: {
				...prev.targetingOptions,
				cities: uniqueCities,
			},
		}))
	}

	const handleSave = () => {
		// Validate that campaign is not in draft state before publishing to Meta
		if (common.view.campaign.status === 'draft') {
			appendToast({
				title: 'Campaign must be approved first',
				description: 'The campaign must be reviewed and approved before publishing to Meta.',
				variant: 'error',
			})
			return
		}

		// The Save button is only reachable once the campaign has loaded (see the
		// loading guard in CampaignTargeting), so these are never null here.
		if (!displayedStartDate || !displayedEndDate) { return }

		// Extract local calendar dates — using YYYY-MM-DD format avoids
		// timezone-shift bugs where .toISOString() moves the date back a day
		// (e.g. Helsinki midnight → UTC previous day 22:00).
		// The backend handles same-day start dates via getSafeStartDate().
		const startDateStr = toLocalDateString(displayedStartDate)
		const endDateStr = toLocalDateString(displayedEndDate)

		if (endDateStr < startDateStr) {
			appendToast({
				title: 'Invalid date range',
				description: 'Campaign end date must be after the start date.',
				variant: 'error',
			})
			return
		}

		launchCampaign.mutate({
			campaign: campaign.id,
			data: {
				campaignId: campaign.id,
				campaignName: campaign.title,
				campaignStartDate: startDateStr,
				campaignEndDate: `${endDateStr}T23:59:59+00:00`,
				advertiseOnFacebook: true,
				advertiseOnInstagram: true,
				adLink: getFormLink(),
				adMessage: feedAd.data?.metaTitle,
				adCaption: feedAd.data?.metaPrimaryText,
				adPicture: campaign.image,
				storyPicture: campaign.storyImage,
				targetingOptions: adCampaign.targetingOptions,
			},
		})
	}

	// eslint-disable-next-line react/hook-use-state
	const [_isCampaignQueued, setIsCampaignQueued] = useState(false)
	const [isCampaignSubmittedToMeta, setIsCampaignSubmittedToMeta] = useState(false)
	const [hasShownErrorModal, setHasShownErrorModal] = useState(false)

	const metaAdCampaign = useMetaAdStructureGetMetaAdCampaignByCampaignId(
		params.campaignId,
		{
			retry: false,
			enabled: !!params.campaignId,
			refetchInterval: (query) => {
				const status = query.state.data?.status
				return status === 'queued' ? 3000 : false
			},
		},
	)

	useEffect(() => {
		const status = metaAdCampaign.data?.status
		const latestError = metaAdCampaign.data?.latestError

		if (status === 'queued') {
			setIsCampaignQueued(true)
			setHasShownErrorModal(false)
		} else {
			setIsCampaignQueued(false)
		}

		if (status === 'queued' || status === 'pushed') {
			setIsCampaignSubmittedToMeta(true)
		} else {
			setIsCampaignSubmittedToMeta(false)
		}

		if (status === 'error' && latestError?.errorMessage && !hasShownErrorModal) {
			errorConfirmationRef.current?.confirm({
				title: 'Campaign Push Failed',
				message: latestError.errorMessage
					|| 'An error occurred while pushing the campaign to Meta. Please try again.',
				confirmText: 'OK',
				cancelText: '',
			}).catch(() => {})
			setHasShownErrorModal(true)
		}
	}, [metaAdCampaign.data?.status, metaAdCampaign.data?.latestError, hasShownErrorModal])

	const facebookDetails = useMetaAdStructureFacebookDetails(
		metaAdCampaign.data?.id ?? '',
		{
			enabled: isCampaignSubmittedToMeta && !!metaAdCampaign.data?.id,
			retry: false,
		},
	)

	return (
		<>
			<CampaignTargeting
				view={{
					startDate: displayedStartDate,
					endDate: displayedEndDate,
					overrideDatesEnabled,
					budget: common.view.campaign.budget ?? 0,
					serviceFeePercent: common.view.campaign.serviceFeePercent,
					budgetBreakdown: campaign.budgetBreakdown,
					addons: campaign.addons,
					interests: adCampaign.targetingOptions.interests as NamedObject<InterestData>[],
					countries: adCampaign.targetingOptions.countries as NamedObject<CountryData>[],
					cities: adCampaign.targetingOptions.cities as NamedObject<CityData>[],
					citySearch,
					countrySearch,
					interestSearch,
					interestSuggestions: interests.data ?? [],
					countrySuggestions: (countries.data ?? []) as unknown as NamedObject<CountryData>[],
					citySuggestions: sortedCitySuggestions as unknown as NamedObject<CityData>[],
					isLoadingInterests: interests.isLoading,
					isLoadingCountries: countries.isLoading,
					isLoadingCities: cities.isLoading,
					hasInterestsError: interests.isError,
					hasCountriesError: countries.isError,
					hasCitiesError: cities.isError,
					reachEstimate: reachEstimate.data,
					isLoadingReachEstimate: reachEstimate.isLoading,
					hasReachEstimateError: reachEstimate.isError,
					isCampaignQueued: isCampaignSubmittedToMeta, // Show Meta dashboard link when queued or pushed
					manageCampaignUrl: facebookDetails.data?.metaUrl,
					isLoadingUrl: facebookDetails.isLoading,
					hasUrlError: facebookDetails.isError,
					isLaunching: launchCampaign.isPending,
				}}
				events={{
					onStartDateChange: (value) => handleStartDateChange(value),
					onEndDateChange: (value) => handleEndDateChange(value),
					onToggleOverrideDates: (value) => handleToggleOverrideDates(value),
					onInterestsChange: (value) => {
						handleInterestsChange(value)
					},
					onCountriesChange: (value) => {
						handleCountriesChange(value)
					},
					onCitiesChange: (value) => {
						handleCitiesChange(value)
					},
					onCitySearchChange: (value) => {
						handleCitySearchChange(value)
					},
					onCountrySearchChange: (value) => {
						handleCountrySearchChange(value)
					},
					onInterestSearchChange: (value) => {
						handleInterestSearchChange(value)
					},
					onSave: () => {
						handleSave()
					},
				}}
				options={{
					cityRadiusLimits: [17, 80],
				}}
				common={common}
			/>
			<ConfirmationDialog ref={errorConfirmationRef} />
		</>
	)
}
